import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph';

import { Block, BlockTitle, Lead, Meta } from './passportStyles';
import { SkillLevelMeter } from './SkillLevelMeter';

/**
 * Паспорт, пункты 1–2 (ТЗ 0072, 3.3): что это и где я — текущий уровень,
 * откуда он и какой нужен.
 *
 * «Зачем профессии» и связи переехали в свёрнутое «Więcej informacji», а
 * фраза «что делать» — в блок основного действия: аудит нашёл несколько
 * повторяющих друг друга объясняющих блоков подряд.
 */

/**
 * Источник уровня — ОТДЕЛЬНОЙ строкой под шкалой. Три разных утверждения:
 * своя оценка, подтверждение школы, упоминание в CV без оценки (0072).
 * Неизвестный источник не называется вовсе: сырое `import` хуже молчания.
 */
const SOURCE_KEY: Record<string, string> = {
  course: 'careerCenter.skillSource.course',
  self: 'careerCenter.skillSource.self',
};

interface ISkillPassportAbout {
  node: ICareerSkillNode;
  /** Детальный запрос: «Wspomniano w CV». `null`, пока не пришёл. */
  mentionedInCv: boolean | null;
  /** 0073: зачем этот навык в профессии карты; пусто — строка не рисуется. */
  usageNote?: string;
}

export const SkillPassportAbout = memo(
  ({ node, mentionedInCv, usageNote = '' }: ISkillPassportAbout) => {
    const { t } = useTranslation();

    const sourceText =
      node.myLevel !== null
        ? SOURCE_KEY[node.source]
          ? t(SOURCE_KEY[node.source])
          : ''
        : mentionedInCv
        ? t('careerCenter.skillSource.mentioned')
        : mentionedInCv === false
        ? t('careerCenter.skillSource.none')
        : '';

    return (
      <>
        {/* 1. Простое объяснение. Пустое описание не заменяется заглушкой:
          «Opis wkrótce» ничего не объясняет и занимает первое место. */}
        {node.description && <Lead>{node.description}</Lead>}
        {usageNote ? (
          <Meta>
            <strong>{t('careerCenter.map.usageTitle')}</strong> {usageNote}
          </Meta>
        ) : null}

        {/* 2. Текущий и требуемый уровень; источник — отдельной строкой. */}
        <Block aria-labelledby="skm-passport-level">
          <BlockTitle id="skm-passport-level">{t('careerCenter.map.levelTitle')}</BlockTitle>
          <SkillLevelMeter myLevel={node.myLevel} requiredLevel={node.requiredLevel} />
          {sourceText && <Meta>{sourceText}</Meta>}
        </Block>
      </>
    );
  },
);
